import {
  MERGE_COUNTS,
  MERGE_DIFF,
  MERGE_REBASE,
  MERGE_ROWS,
  MERGE_SUBMIT,
} from 'src/url';
import * as API from 'src/services/API';

import { SUBMIT_ROUTE_ENABLED } from '../config';

import {
  MergeCountsDto,
  MergeCountsRequest,
  MergeDiffRequest,
  MergeDiffResponse,
  MergePageDto,
  MergeRebaseRequest,
  MergeRebaseResponse,
  MergeRowsRequest,
  MergeSubmitRequest,
  MergeSubmitResponse,
} from './mergeResolution.types';
import { MergeApiError, toMergeApiError } from './MergeApiError';
import {
  MergeResolutionDataSource,
  MergeRouteIds,
} from './MergeResolutionDataSource';

/**
 * Posts a merge request, passing the body through unchanged and attaching an
 * `AbortSignal` only when one is given.
 *
 * The two call shapes are deliberate: with no signal, `API.create` is called
 * with two arguments, which is what every other caller in the app does and
 * what this module's tests assert. Passing an explicit `undefined` fourth
 * argument would be equivalent at runtime but would change the recorded call.
 */
async function post<T>(
  path: string,
  req: object,
  signal?: AbortSignal
): Promise<T> {
  try {
    return signal
      ? await API.create<T>(path, req, undefined, { signal })
      : await API.create<T>(path, req);
  } catch (err) {
    throw toMergeApiError(err);
  }
}

export class ApiMergeResolutionDataSource implements MergeResolutionDataSource {
  async diff(
    ids: MergeRouteIds,
    req: MergeDiffRequest,
    signal?: AbortSignal
  ): Promise<MergeDiffResponse> {
    return post<MergeDiffResponse>(
      MERGE_DIFF(ids.instanceId, ids.scenarioId),
      req,
      signal
    );
  }

  async rows(
    ids: MergeRouteIds,
    req: MergeRowsRequest,
    signal?: AbortSignal
  ): Promise<MergePageDto> {
    return post<MergePageDto>(
      MERGE_ROWS(ids.instanceId, ids.scenarioId),
      req,
      signal
    );
  }

  async counts(
    ids: MergeRouteIds,
    req: MergeCountsRequest,
    signal?: AbortSignal
  ): Promise<MergeCountsDto> {
    return post<MergeCountsDto>(
      MERGE_COUNTS(ids.instanceId, ids.scenarioId),
      req,
      signal
    );
  }

  async rebase(
    ids: MergeRouteIds,
    req: MergeRebaseRequest,
    signal?: AbortSignal
  ): Promise<MergeRebaseResponse> {
    return post<MergeRebaseResponse>(
      MERGE_REBASE(ids.instanceId, ids.scenarioId),
      req,
      signal
    );
  }

  async submit(
    ids: MergeRouteIds,
    req: MergeSubmitRequest,
    signal?: AbortSignal
  ): Promise<MergeSubmitResponse> {
    // Kill switch: with the flag off, fail closed without touching the
    // network. The route ships with CLD-2971, and the flag is on.
    if (!SUBMIT_ROUTE_ENABLED) {
      return Promise.reject(new MergeApiError('SUBMIT_UNAVAILABLE', 0));
    }

    return post<MergeSubmitResponse>(
      MERGE_SUBMIT(ids.instanceId, ids.scenarioId),
      req,
      signal
    );
  }
}
